import { formatPhoneNumber, strToBoolean } from "#src/util/helpers.js";

import {
  SMOKING_CURRENTLY,
  SMOKING_FREQUENCIES,
  SMOKING_PREVIOUSLY,
  SMOKING_STATUSES,
} from "#src/structures/SmokingUsage.js";

import { STATES, COUNTRIES } from "#src/data/states-and-countries.js";
import { useInsuredStore } from "#src/stores/insured.js";
import {
  smokingUsageNameGenerator,
  useSmokingUsage,
} from "#src/stores/insured-smoking-usage.js";
import { ValidationsService } from "#src/services/validations.service.js";

export class InsuredPrefillService {
  #pinia;
  #validationsService;

  constructor(pinia) {
    this.#pinia = pinia;
    this.#validationsService = new ValidationsService(pinia);
  }

  static PRE_CREATE_KEYS = [
    "first_name",
    "last_name",
    "gender",
    "birthdate",
    "height",
    "weight",
    "marital_status",
    "smoker",
    "smoking_statuses",
    "email",
    "phone_mobile",
    "health",
  ];

  async setFromPrefill(
    insuredType,
    defaults,
    { isPreCreate = false, skipSetValues = false } = {}
  ) {
    const insured = useInsuredStore(insuredType, this.#pinia);
    if (!defaults || !Object.keys(defaults).length) return;

    const normalizedPrefill = this.#normalize(defaults);

    const stateKeys = Object.keys(insured.$state).filter(
      v => insured.$state[v]?.load
    );
    const prefillKeys = Object.keys(normalizedPrefill);

    const attributesToUpdate = [];
    const unchangedAttributes = [];
    const invalidAttributes = {};

    // Perform assignment here
    for (const key of stateKeys) {
      if (!prefillKeys.includes(key)) continue;
      if (isPreCreate && !InsuredPrefillService.PRE_CREATE_KEYS.includes(key))
        continue;

      if (insured[key].model !== null && skipSetValues) {
        unchangedAttributes.push(key);
      } else if (insured[key].model === normalizedPrefill[key]) {
        unchangedAttributes.push(key);
      } else if (key === "email" && normalizedPrefill.email) {
        const valid = await this.#validationsService.validateEmail(
          normalizedPrefill.email
        );
        if (valid) insured.email.load(normalizedPrefill.email);
      } else {
        insured[key].model = normalizedPrefill[key];
      }
    }

    if (normalizedPrefill.smoking_statuses?.length) {
      attributesToUpdate.push("smoking_statuses");
      insured.smoking_statuses.splice(0, insured.smoking_statuses.length);
      normalizedPrefill.smoking_statuses.forEach((smokingUsage, index) => {
        const id = smokingUsageNameGenerator();
        const usage = useSmokingUsage(id, this.#pinia);
        let atLeastOneValid = false;
        let keysToValidate = ["frequency", "category"];
        if (insured.smoker.model === SMOKING_PREVIOUSLY) {
          keysToValidate.push("last_use_date");
        }
        keysToValidate.forEach(key => {
          usage[key].load(smokingUsage[key]);
          if (usage[key].validate()) {
            atLeastOneValid = true;
          } else {
            usage[key].model = null;
            invalidAttributes[`smoking_usage.${index}.${key}`] =
              usage[key].errorMessages;
          }
        });

        if (atLeastOneValid) insured.smoking_statuses.push(id);
      });
    }

    // loop through to confirm results of dependent validations / async validators
    for (const key of stateKeys) {
      if (!prefillKeys.includes(key)) continue;
      if (unchangedAttributes.includes(key)) continue;

      const isValid = insured[key].validate();
      if (isValid) {
        attributesToUpdate.push(key);
      } else {
        invalidAttributes[key] = insured[key].errorMessages;
        insured[key].load(null);
      }
    }

    if (attributesToUpdate.includes("drivers_license_has")) {
      insured.drivers_license_has.model = normalizedPrefill.drivers_license_has;
    }

    let invalid = invalidAttributes;
    if (!Object.keys(invalid).length) invalid = null;
    return { valid: attributesToUpdate, invalid };
  }

  async saveData(insuredType, attributesToUpdate) {
    const insured = useInsuredStore(insuredType, this.#pinia);

    const savableKeys = Object.keys(insured.$state).filter(
      v => insured.$state[v]?.load
    );
    const keysToSave = savableKeys.filter(v => attributesToUpdate.includes(v));

    const returnData = {};

    if (attributesToUpdate.includes("smoking_statuses")) {
      await insured.saveSmokingStatuses();

      returnData["smoking_statuses"] = insured.smoking_statuses.map(
        smokingUsageId => {
          const smokingUsage = useSmokingUsage(smokingUsageId, this.#pinia);
          return {
            frequency: smokingUsage.frequency.model,
            category: smokingUsage.category.model,
            last_use_date: smokingUsage.last_use_date.model,
          };
        }
      );
    }

    if (!keysToSave.length) return;
    try {
      await insured.saveAttributes(keysToSave);
    } catch (e) {
      keysToSave.forEach(attr => insured[attr].load(null));
      throw e;
    }

    keysToSave.forEach(attr => {
      returnData[attr] = insured[attr].model;
    });
    return returnData;
  }

  #normalize(initPrefill) {
    const prefill = { ...initPrefill };
    // remapping
    prefill.birth_location = prefill.birth_state_or_country;
    prefill.drivers_license_number =
      prefill.drivers_license || prefill.drivers_license_number;
    delete prefill.birth_state_or_country;

    // coercion
    if (prefill.health) prefill.health = +prefill.health;
    if (prefill.height) prefill.height = +prefill.height;
    if (prefill.weight) prefill.weight = +prefill.weight;
    if (prefill.assets) prefill.assets = +prefill.assets;
    if (prefill.income) prefill.income = +prefill.income;
    if (prefill.liabilities) prefill.liabilities = +prefill.liabilities;
    if (prefill.years_at_address || prefill.years_at_address === "0") {
      prefill.years_at_address = +prefill.years_at_address;
    }

    // conversion
    if (prefill.phone) prefill.phone_mobile = formatPhoneNumber(prefill.phone);
    if (prefill.bankruptcy)
      prefill.bankruptcy = strToBoolean(prefill.bankruptcy);
    if (prefill.us_citizen)
      prefill.us_citizen = strToBoolean(prefill.us_citizen);
    if (prefill.send_client_intro_message)
      prefill.send_client_intro_message = strToBoolean(
        prefill.send_client_intro_message
      );

    // case insensitive match
    if (prefill.gender) {
      prefill.gender = {
        male: "Male",
        female: "Female",
      }[prefill.gender.toLowerCase()];
    }

    // complex conversions
    if (prefill.smoker) {
      const smoker = SMOKING_STATUSES.find(
        v => v.value.toLowerCase() === prefill.smoker.toLowerCase()
      );
      if (smoker) prefill.smoker = smoker.value;
      const canHaveSmokingStatuses = [
        SMOKING_PREVIOUSLY,
        SMOKING_CURRENTLY,
      ].includes(prefill.smoker);

      try {
        const hasDefinedStatuses = prefill.smoking_statuses?.length;
        if (canHaveSmokingStatuses && hasDefinedStatuses) {
          let statuses = prefill.smoking_statuses;
          if (typeof statuses === "string") {
            statuses = JSON.parse(statuses);
          }

          prefill.smoking_statuses = statuses.map(rawStatus => {
            let frequency = SMOKING_FREQUENCIES.find(frequency => {
              return [`${frequency.value}`, `${frequency.title}`].includes(
                `${rawStatus.frequency}`
              );
            });

            if (frequency) frequency = frequency.value;

            return {
              frequency,
              category: rawStatus.category,
              last_use_date: rawStatus.last_use_date,
            };
          });
        }
        // eslint-disable-next-line no-unused-vars
      } catch (e) {
        prefill.smoking_statuses = [];
      }
    }

    if (!Array.isArray(prefill.smoking_statuses)) prefill.smoking_statuses = [];

    // birth_location can be full or abbreviated state. must be full for non US
    if (prefill.birth_location && `${prefill.birth_location}`.length === 2) {
      const foundState = STATES.find(({ title, value }) =>
        [value, title].includes(prefill.birth_location)
      );
      if (foundState) {
        prefill.birth_location = foundState.value;
        prefill.us_citizen = true;
        prefill.country_of_citizenship = "United States";
      } else {
        prefill.birth_location = "";
      }
    } else if (prefill.birth_location) {
      // it's a title/value Object array
      const foundCountry = COUNTRIES.find(
        ({ value }) => prefill.birth_location === value
      );
      if (foundCountry) {
        prefill.country_of_citizenship = prefill.birth_location;
      } else {
        prefill.birth_location = "";
      }
    }

    prefill.drivers_license_has = undefined;
    // DL State must be 2 character state abbreviation & in list
    if (prefill.drivers_license_number && prefill.drivers_license_state) {
      prefill.drivers_license_has = true;
    } else {
      delete prefill.drivers_license_number;
      delete prefill.drivers_license_state;
      delete prefill.drivers_license_expiration;
    }

    if (!prefill.drivers_license_has && prefill.no_drivers_license_reason) {
      prefill.drivers_licenseless_reason = prefill.no_drivers_license_reason;
      prefill.drivers_license_has = false;
    }

    const data = {
      email: prefill?.email,
      height: prefill?.height,
      weight: prefill?.weight,
      first_name: prefill?.first_name,
      last_name: prefill?.last_name,
      beneficiary_amount: prefill?.beneficiary_amount,
      gender: prefill?.gender,
      phone_mobile: prefill?.phone_mobile,
      birthdate: prefill?.birthdate,
      street_address: prefill?.street_address,
      city: prefill?.city,
      state: prefill?.state,
      zip: prefill?.zip,
      country: prefill?.country,
      years_at_address: prefill?.years_at_address,
      exam_street_address: prefill?.exam_street_address,
      exam_city: prefill?.exam_city,
      exam_state: prefill?.exam_state,
      exam_zip: prefill?.exam_zip,
      exam_country: prefill?.exam_country,
      signature_city: prefill?.signature_city,
      ssn: prefill?.ssn,
      assets: prefill?.assets,
      bankruptcy_discharge_date: prefill?.bankruptcy_discharge_date,
      bankruptcy_type: prefill?.bankruptcy_type,
      bankruptcy: prefill?.bankruptcy,
      birth_location: prefill?.birth_location,
      country_of_citizenship: prefill?.country_of_citizenship,
      drivers_license_has: prefill?.drivers_license_has,
      drivers_license_state: prefill?.drivers_license_state,
      drivers_license_number: prefill?.drivers_license_number,
      drivers_license_expiration: prefill?.drivers_license_expiration,
      drivers_licenseless_reason: prefill?.drivers_licenseless_reason,
      liabilities: prefill?.liabilities,
      income: prefill?.income,
      us_citizen: prefill?.us_citizen,
      us_entry_date: prefill?.us_entry_date,
      visa_type: prefill?.visa_type,
      account_number: prefill?.account_number,
      routing_number: prefill?.routing_number,
      eft: prefill?.eft,
      health: prefill?.health,
      marital_status: prefill?.marital_status,
      relationship: prefill?.relationship,
      smoker: prefill?.smoker,
      smoking_statuses: prefill?.smoking_statuses,
      send_client_intro_message: prefill?.send_client_intro_message,
    };

    Object.keys(data).forEach(d => {
      if (data[d] === undefined) delete data[d];
    });

    return data;
  }
}
